/**
 * 権限管理の管理画面 (ユーザー管理 / 権限グループ管理 / 権限付与) のエラーメッセージを日本語に変換するモジュール (権限管理 SBI-8)。
 *
 * バックエンド (`lambda/control-panel-api/index.py`) は英語の文を `message` として返す。そのまま画面に
 * 出すと何を直せばよいか伝わらないため、日本語の文と原因の入力欄に変換する。
 *
 * - 400: 入力検証エラー。原因欄が分かるものは `field` を返す (欄の直下に出す)。
 * - 409: 最後の管理者 / 最後の管理権限グループ / 所属ユーザーあり / 招待済み。ロックアウト防止の
 *   拒否は「どうすればよいか」まで書く。
 * - 原因欄が特定できないもの: バナーに出す。
 *
 * 対応表 (`SERVER_MESSAGE_RULES`) は `index.py` の `ValueError(...)` / `_error(400|409, ...)` と対になる。
 * 更新漏れに気づけるよう、`permissionAdminMessages.test.ts` が `index.py` を読んで突き合わせている。
 */
import { ApiError } from '../api/common';

import { NETWORK_ERROR_MESSAGE, UNEXPECTED_ERROR_MESSAGE } from './freeTextTemplateMessages';

/** エラーの原因になった入力欄 */
export type PermissionAdminErrorField = 'name' | 'description' | 'email';

/** 画面表示用に整形したエラー */
export interface PermissionAdminErrorInfo {
  /** 利用者向けの日本語メッセージ */
  message: string;
  /** 原因の入力欄。特定できない場合は null (バナー表示にする) */
  field: PermissionAdminErrorField | null;
}

/** 必須項目が空のときのメッセージ (クライアント側の検証とサーバ文言の対応表で共用する) */
export const PERMISSION_ADMIN_REQUIRED_MESSAGES = {
  name: 'グループ名を入力してください',
  email: 'メールアドレスを入力してください',
} as const;

/** 対象の指定自体が不正なとき (画面からは発生しないが念のため対応表に持つ) */
const INVALID_TARGET_MESSAGE = '対象の指定が正しくありません。一覧を再読み込みしてください';

interface ServerMessageRule {
  /** バックエンドの英語メッセージ全体に一致させる正規表現 */
  pattern: RegExp;
  field: PermissionAdminErrorField | null;
  message: (match: RegExpMatchArray) => string;
}

/** バックエンドの 400 / 409 メッセージ → 日本語 + 対象欄の対応表 */
export const SERVER_MESSAGE_RULES: readonly ServerMessageRule[] = [
  // ---- 権限グループ: 名称 / 説明 ----
  {
    pattern: /^name is required$/,
    field: 'name',
    message: () => PERMISSION_ADMIN_REQUIRED_MESSAGES.name,
  },
  {
    pattern: /^name must be a string$/,
    field: 'name',
    message: () => 'グループ名は文字列で入力してください',
  },
  {
    pattern: /^name exceeds (\S+) characters$/,
    field: 'name',
    message: (m) => `グループ名は${m[1]}文字以内で入力してください`,
  },
  {
    pattern: /^name already exists$/,
    field: 'name',
    message: () => '同じ名前の権限グループがすでにあります。別の名前にしてください',
  },
  {
    pattern: /^description must be a string$/,
    field: 'description',
    message: () => '説明は文字列で入力してください',
  },
  {
    pattern: /^description exceeds (\S+) characters$/,
    field: 'description',
    message: (m) => `説明は${m[1]}文字以内で入力してください`,
  },
  // ---- 権限グループ: 機能 ----
  {
    pattern: /^features must be an array( of strings)?$/,
    field: null,
    message: () => '機能の指定が正しくありません。画面を再読み込みしてからやり直してください',
  },
  {
    pattern: /^unknown feature key\(s\): .+$/,
    field: null,
    message: () =>
      '選択した機能の中に、現在は利用できないものがあります。画面を再読み込みしてからやり直してください',
  },
  {
    pattern: /^at least one of name, description, features is required$/,
    field: null,
    message: () => '更新する項目がありません',
  },
  // ---- ユーザーの招待 ----
  {
    pattern: /^email is required$/,
    field: 'email',
    message: () => PERMISSION_ADMIN_REQUIRED_MESSAGES.email,
  },
  {
    pattern: /^email must be a string$/,
    field: 'email',
    message: () => 'メールアドレスは文字列で入力してください',
  },
  {
    pattern: /^email exceeds (\S+) characters$/,
    field: 'email',
    message: (m) => `メールアドレスは${m[1]}文字以内で入力してください`,
  },
  {
    pattern: /^email is invalid$/,
    field: 'email',
    message: () => 'メールアドレスの形式が正しくありません',
  },
  {
    pattern: /^User already exists$/,
    field: 'email',
    message: () => 'このメールアドレスのユーザーはすでに登録されています',
  },
  // ---- ユーザーの無効化 / 削除 ----
  {
    pattern: /^You cannot (\S+) yourself$/,
    field: null,
    message: (m) =>
      m[1] === 'delete'
        ? '自分自身は削除できません。別の管理者に依頼してください'
        : '自分自身は無効化できません。別の管理者に依頼してください',
  },
  {
    pattern: /^Cannot (\S+) the last administrator$/,
    field: null,
    message: (m) =>
      `最後の管理者は${m[1] === 'delete' ? '削除' : '無効化'}できません。誰も権限を管理できなくなるためです。` +
      '先に別のユーザーへ管理系の機能を持つグループを付与してください',
  },
  // ---- 権限グループの削除 / 機能の除去 ----
  {
    pattern: /^Cannot delete the last admin permission group$/,
    field: null,
    message: () =>
      '最後の管理権限グループは削除できません。誰も権限を管理できなくなるためです。' +
      '先に別のグループへ管理系の機能を設定してください',
  },
  {
    pattern: /^Cannot remove admin features from the last admin permission group$/,
    field: null,
    message: () =>
      '最後の管理権限グループからは、管理系の機能 (ユーザー管理 / 権限グループ管理 / 権限付与) を外せません。' +
      '誰も権限を管理できなくなるためです',
  },
  {
    pattern: /^Permission group has members; remove them first$/,
    field: null,
    message: () =>
      'このグループには所属ユーザーがいるため削除できません。先に権限付与画面で所属を外してください',
  },
  // ---- 権限付与 ----
  {
    pattern: /^Cannot remove the last administrator's admin permissions$/,
    field: null,
    message: () =>
      '最後の管理者から管理権限を外すことはできません。誰も権限を管理できなくなるためです。' +
      '先に別のユーザーへ管理系の機能を持つグループを付与してください',
  },
  {
    pattern: /^group_ids (is required|must be an array|must be an array of UUIDs)$/,
    field: null,
    message: () =>
      '所属グループの指定が正しくありません。画面を再読み込みしてからやり直してください',
  },
  {
    pattern: /^group_ids exceeds (\S+) items$/,
    field: null,
    message: (m) => `所属できるグループは最大${m[1]}件です`,
  },
  {
    pattern: /^unknown group_id\(s\): .+$/,
    field: null,
    message: () =>
      '選択したグループの中に、すでに削除されたものがあります。一覧を再読み込みしてからやり直してください',
  },
  // ---- 対象の指定 (パスパラメータ) ----
  {
    pattern: /^(group_id|user_sub) (is required|must be a UUID)$/,
    field: null,
    message: () => INVALID_TARGET_MESSAGE,
  },
];

/** バックエンドの 400 / 409 メッセージを日本語 + 対象欄に変換する。対応表に無ければ null。 */
export function translateServerMessage(serverMessage: string): PermissionAdminErrorInfo | null {
  for (const rule of SERVER_MESSAGE_RULES) {
    const match = serverMessage.match(rule.pattern);
    if (match) return { message: rule.message(match), field: rule.field };
  }
  return null;
}

/** 日本語の文字 (ひらがな / カタカナ / 漢字) を含むか */
function containsJapanese(text: string): boolean {
  return /[\u3040-\u30ff\u3400-\u9fff]/.test(text);
}

/** HTTP ステータスとサーバのメッセージから、画面表示用のエラーを作る。 */
function describeHttpError(status: number, serverMessage: string): PermissionAdminErrorInfo {
  if (status === 400 || status === 409) {
    const translated = translateServerMessage(serverMessage);
    if (translated) return translated;
    const fallback = containsJapanese(serverMessage)
      ? serverMessage
      : status === 409
        ? `現在の状態ではこの操作を実行できません(詳細: ${serverMessage})`
        : `入力内容を確認してください(詳細: ${serverMessage})`;
    return { message: fallback, field: null };
  }
  if (status === 401) {
    return {
      message: 'ログインの有効期限が切れました。再度ログインしてください',
      field: null,
    };
  }
  if (status === 403) {
    return {
      message:
        'この操作を行う権限がありません。権限が変更された可能性があるため、ページを再読み込みしてください',
      field: null,
    };
  }
  if (status === 404) {
    return {
      message:
        '対象が見つかりません。すでに削除された可能性があります。一覧を再読み込みしてください',
      field: null,
    };
  }
  if (status >= 500) {
    return {
      message: 'サーバでエラーが発生しました。時間をおいて、もう一度お試しください',
      field: null,
    };
  }
  return { message: `処理に失敗しました(HTTP ${status})`, field: null };
}

/**
 * API クライアントの例外を、画面表示用の日本語メッセージ + 対象欄に変換する。
 *
 * - `ApiError`: ステータス別に変換 (400 / 409 は対応表で欄まで特定する)
 * - `TypeError`: `fetch` が通信失敗時に投げる例外 → ネットワーク確認の案内
 */
export function describeAdminError(err: unknown): PermissionAdminErrorInfo {
  if (err instanceof ApiError) return describeHttpError(err.status, err.message);
  if (err instanceof TypeError) return { message: NETWORK_ERROR_MESSAGE, field: null };
  if (err instanceof Error) {
    return {
      message: containsJapanese(err.message)
        ? err.message
        : `${UNEXPECTED_ERROR_MESSAGE}(詳細: ${err.message})`,
      field: null,
    };
  }
  return { message: UNEXPECTED_ERROR_MESSAGE, field: null };
}
